/* Setup tab: Campaigns (a compact table — on/off, how the lists are weighted,
   timing, sends, "See schedule") above Message lists (rows that open in place
   to edit name, vaults, attach-media chance and messages). */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Btn, ErrorLine, Icon, Loading, Toggle } from '../../ChatAssistant/compact/kit';
import { font as f } from '../../ChatAssistant/compact/style';
import { C } from '../../ChatAssistant/compact/tokens';
import { CampaignModal } from './CampaignModal';
import { ListsSection } from './ListsSection';
import { massApi } from './massApi';
import type { MassData } from './MassMessagesPage';
import { DELETED_COLOR, blankCamp, listColor, queuedByCampaign, type Camp, type Kind, type Platform } from './model';
import { IconBtn, SectionHead } from './parts';

const COLS = '40px minmax(170px,1.1fr) minmax(240px,1.7fr) 120px 64px 200px';

export function SetupPage({ kind, accountId, platform, data, access, canManage, onSeeSchedule, onCopyLists, onCopyCampaigns }: {
  kind: Kind; accountId: string; platform: Platform; data: MassData;
  access: { lists: boolean; campaigns: boolean; schedule: boolean }; canManage: boolean;
  onSeeSchedule?: (campaignId: number) => void; onCopyLists: () => void; onCopyCampaigns: () => void;
}) {
  const { t } = useTranslation('massCompact');
  const a = useMemo(() => massApi(kind, accountId), [kind, accountId]);
  const [editing, setEditing] = useState<Camp | null>(null);

  if (data.loading) return <div className="cpt-root" style={{ padding: '0 20px' }}><Loading /></div>;
  if (data.failed) {
    return (
      <div className="cpt-root" style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
        <ErrorLine>{t('loadFailed')}</ErrorLine>
        <Btn icon="refresh-cw" onClick={() => { void data.reload(); }}>{t('retry')}</Btn>
      </div>
    );
  }

  return (
    <div className="cpt-root">
      <div style={{ padding: '6px 4px 56px', width: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 28 }}>
        {access.campaigns && (
          <Campaigns
            kind={kind}
            data={data}
            canManage={canManage}
            onSeeSchedule={onSeeSchedule}
            onCopy={onCopyCampaigns}
            onNew={() => setEditing(blankCamp(kind))}
            onEdit={(c) => setEditing(JSON.parse(JSON.stringify(c)))}
            toggle={async (c) => {
              data.setCamps((cs) => cs.map((x) => (x.id === c.id ? { ...x, active: !c.active } : x)));
              try { await a.toggleCampaign(c.id!, !c.active); void data.reloadRows(true); }
              catch (e) { console.error('Failed to toggle campaign:', e); alert(t('camp.toggleFailed')); void data.reload(); }
            }}
            remove={async (c) => {
              if (!confirm(t('camp.deleteConfirm', { name: c.name }))) return;
              try { await a.deleteCampaign(c.id!); data.setCamps((cs) => cs.filter((x) => x.id !== c.id)); data.setRows((rs) => rs.filter((r) => r.campaign_id !== c.id)); }
              catch (e) { console.error('Failed to delete campaign:', e); alert(t('camp.deleteFailed')); }
            }}
          />
        )}
        {access.lists && <ListsSection kind={kind} accountId={accountId} data={data} onCopy={onCopyLists} />}
      </div>

      {editing && (
        <CampaignModal
          kind={kind}
          accountId={accountId}
          platform={platform}
          initial={editing}
          lists={data.lists}
          onClose={() => setEditing(null)}
          onSave={async (c, body) => {
            if (c.id === null) await a.createCampaign(body);
            else await a.updateCampaign(c.id, body);
            setEditing(null);
            await data.reload();
          }}
        />
      )}
    </div>
  );
}

function Campaigns({ kind, data, canManage, onSeeSchedule, onCopy, onNew, onEdit, toggle, remove }: {
  kind: Kind; data: MassData; canManage: boolean; onSeeSchedule?: (id: number) => void; onCopy: () => void; onNew: () => void;
  onEdit: (c: Camp) => void; toggle: (c: Camp) => void; remove: (c: Camp) => void;
}) {
  const { t } = useTranslation('massCompact');
  const listIdx = new Map(data.lists.map((l, i) => [String(l.id), i]));
  const queued = queuedByCampaign(data.rows);
  const nameOf = (id: string) => { const i = listIdx.get(id); return i === undefined ? t('camp.deletedList') : data.lists[i]!.name; };
  const colorOf = (id: string) => { const i = listIdx.get(id); return i === undefined ? DELETED_COLOR : listColor(i); };
  const active = data.camps.filter((c) => c.active).length;

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <SectionHead title={t('camp.title')} sub={t('camp.summary', { count: data.camps.length, active })}>
        {canManage && <Btn icon="copy" onClick={onCopy} style={{ background: C.input, borderColor: C.border }}>{t('copyFromAccount')}</Btn>}
        {canManage && <Btn kind="primary" icon="plus" onClick={onNew}>{t('camp.new')}</Btn>}
      </SectionHead>
      <div className="cpt-thin-scroll" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, overflowX: 'auto' }}>
        <div style={{ minWidth: 900 }}>
          <div style={{ display: 'grid', gridTemplateColumns: COLS, alignItems: 'center', columnGap: 14, height: 32, padding: '0 14px', font: f(600, 10), letterSpacing: '.08em', textTransform: 'uppercase', color: C.muted }}>
            <span /><span>{t('camp.colCampaign')}</span><span>{t('camp.colLists')}</span><span>{t('camp.colTiming')}</span><span style={{ textAlign: 'right' }}>{t('camp.colSends')}</span><span />
          </div>
          {data.camps.map((c) => {
            const auto = kind === 'mass' && c.source === 'auto';
            const live = c.dist.filter((d) => listIdx.has(d.id));
            const allDel = !auto && c.dist.length > 0 && !live.length;
            const q = queued.get(c.id!) ?? 0;
            return (
              <div key={c.id} style={{ display: 'grid', gridTemplateColumns: COLS, alignItems: 'center', columnGap: 14, padding: '11px 14px', borderTop: `1px solid ${C.divider}` }}>
                <Toggle on={c.active} disabled={!canManage} onChange={() => toggle(c)} title={c.active ? t('camp.onTip') : t('camp.offTip')} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                  <span title={c.name} style={{ font: f(600, 13), color: c.active ? C.text : C.sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</span>
                  <span style={{ font: f(400, 11.5), color: C.muted, whiteSpace: 'nowrap' }}>{auto ? t('camp.autoSource') : t('camp.lists', { count: c.dist.length })} · {t('camp.audiences', { count: c.inc.length })}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 2, height: 6 }}>
                    {auto ? <span style={{ width: '100%', background: 'var(--om-seg)', borderRadius: 2 }} />
                      : c.dist.map((d) => <span key={d.id} style={{ width: `${d.w}%`, background: colorOf(d.id), borderRadius: 2 }} />)}
                  </div>
                  {allDel ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, font: f(500, 11.5), color: C.purplePale }}><Icon name="alert-circle" size={12} style={{ flex: 'none' }} />{t('camp.allDeleted', { count: c.dist.length })}</span>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 10, rowGap: 2, font: f(400, 11.5), color: C.sub }}>
                      {auto ? <Legend color="var(--om-seg)" name={t('camp.autoLegend')} />
                        : c.dist.slice(0, 3).map((d) => <Legend key={d.id} color={colorOf(d.id)} name={nameOf(d.id)} w={`${d.w}%`} />)}
                      {!auto && c.dist.length > 3 && <span style={{ color: C.muted }}>{t('camp.more', { count: c.dist.length - 3 })}</span>}
                      {!auto && !c.dist.length && <span style={{ color: C.muted }}>{t('camp.noLists')}</span>}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, font: f(400, 11.5), color: C.sub, whiteSpace: 'nowrap' }}>
                  <span title={t('camp.visTip')} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Icon name="eye" size={12} color={C.muted} />
                    {c.keep ? <span style={{ color: C.text, fontWeight: 600 }}>{t('camp.kept')}</span> : <><span style={{ color: C.text, fontWeight: 600 }}>{t('min', { a: c.vis[0], b: c.vis[1] })}</span>{t('camp.visible')}</>}
                  </span>
                  <span title={t('camp.gapTip')} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Icon name="clock" size={12} color={C.muted} /><span style={{ color: C.text, fontWeight: 600 }}>{t('min', { a: c.gap[0], b: c.gap[1] })}</span>{t('camp.gap')}
                  </span>
                </div>
                <span style={{ textAlign: 'right', font: f(600, 13), color: C.text, fontVariantNumeric: 'tabular-nums' }}>{c.sends.toLocaleString()}</span>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                  {onSeeSchedule && (
                    <button type="button" onClick={() => onSeeSchedule(c.id!)} className="cpt-hover" style={{ height: 28, padding: '0 10px', border: `1px solid ${C.borderHi}`, borderRadius: 7, background: C.input, color: C.purplePale, font: f(500, 12), cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                      <Icon name="calendar" size={13} />{t('camp.seeSchedule')}
                      {q > 0 && <span style={{ font: f(600, 10.5), color: C.sub, fontVariantNumeric: 'tabular-nums' }}>{q}</span>}
                    </button>
                  )}
                  {canManage && <IconBtn icon="edit-2" title={t('camp.edit')} onClick={() => onEdit(c)} />}
                  {canManage && <IconBtn icon="trash-2" title={t('camp.delete')} onClick={() => remove(c)} />}
                </div>
              </div>
            );
          })}
          {!data.camps.length && <div style={{ padding: '22px 14px', borderTop: `1px solid ${C.divider}`, font: f(400, 12.5), color: C.sub, textAlign: 'center' }}>{t('camp.none')}</div>}
        </div>
      </div>
    </section>
  );
}

function Legend({ color, name, w }: { color: string; name: string; w?: string }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
      <span style={{ width: 7, height: 7, borderRadius: 2, background: color }} />
      {name}
      {w && <span style={{ fontWeight: 600, color: C.text, fontVariantNumeric: 'tabular-nums' }}>{w}</span>}
    </span>
  );
}
